import React from 'react';
import { Modal, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';

import { GlassSurface } from '../../components/ui/glass';
import { colors, spacing } from '../../core/theme/tokens';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { OnboardingModalContent } from './OnboardingModalContent';

const PANEL_PADDING_V = spacing.lg + spacing.md;

/** Web: centered glass panel on desktop; compact sheet-like panel on narrow viewports. */
export function OnboardingModal() {
  const { width, height } = useWindowDimensions();
  const { isAuthenticated } = useAuth();
  const { isModalVisible, browseSample, closeModal } = useOnboarding();
  const isWide = width >= 768;
  const panelWidth = Math.min(isWide ? 520 : width - 32, width - 24);
  const panelMaxHeight = Math.round(height * (isWide ? 0.8 : 0.9));
  const contentMaxHeight = Math.max(280, panelMaxHeight - PANEL_PADDING_V);

  const handleRequestClose = async () => {
    await browseSample();
  };

  // Guests never see a first-launch modal — sample dashboard + SampleDataBanner only.
  // Signed-in users can still reopen for manual country / holdings entry.
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Modal
      visible={isModalVisible}
      transparent
      animationType="fade"
      onRequestClose={handleRequestClose}
    >
      <View style={styles.backdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleRequestClose}
          accessibilityLabel="Dismiss onboarding"
        />
        <View
          style={[
            styles.panelWrap,
            { width: panelWidth, height: panelMaxHeight },
          ]}
          pointerEvents="box-none"
        >
          <View style={styles.panelHit} pointerEvents="auto">
            <GlassSurface
              tint="neutral"
              rounded="xl"
              showHairline
              style={styles.panel}
              contentStyle={styles.panelContent}
            >
              <OnboardingModalContent
                onRequestClose={closeModal}
                maxHeight={contentMaxHeight}
              />
            </GlassSurface>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.md,
  },
  panelWrap: {
    zIndex: 2,
  },
  panelHit: {
    flex: 1,
  },
  panel: {
    flex: 1,
    height: '100%',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    backgroundColor: colors.surfaceElevated,
  },
  panelContent: {
    flex: 1,
    minHeight: 0,
  },
});
